/*
  Gaia Iluka — shared scroll-reveal observer
  File: assets/gi-reveal.js

  Single IntersectionObserver-based utility replacing seven previously
  duplicated per-section implementations (gi-homepage, origin, quality,
  our-story, harvest-countdown, culinary-partners, gi-editorial). Loaded
  once, site-wide, instead of once per section instance.

  Supports the three reveal conventions already in use across the theme,
  unchanged, so no section markup or CSS needed to change:
    - ".reveal"        -> adds "visible"                  (origin, quality,
                           our-story, harvest-countdown)
    - ".gi-reveal"      -> adds "gi-on" AND "gi-visible"   (gi-homepage uses
                           gi-on, culinary-partners uses gi-visible — both
                           are added so either section's existing CSS keeps
                           working without modification)
    - "[data-reveal]"   -> adds "is-visible"               (gi-editorial)

  Also supports the new canonical convention from the shared design
  system (assets/gi-design-system.css), for sections migrated during
  the upcoming page-by-page redesign pass:
    - ".gids-reveal"    -> adds "gids-in"

  Elements inside a revealed target matching .qc-bar-fill, .or-variety-fill
  or .or-note-fill also get an "animate" class, preserving the bar-chart
  fill animations that used to be wired up ad hoc inside quality.liquid and
  origin.liquid.
*/
(function () {
  'use strict';

  var BAR_SELECTOR = '.qc-bar-fill, .or-variety-fill, .or-note-fill';

  function activateBars(target) {
    if (!target.querySelectorAll) return;
    target.querySelectorAll(BAR_SELECTOR).forEach(function (f) {
      f.classList.add('animate');
    });
  }

  var noObserver = !('IntersectionObserver' in window);

  function reveal(selector, classes) {
    var els = document.querySelectorAll(selector);
    if (!els.length) return;

    if (noObserver) {
      els.forEach(function (el) {
        classes.forEach(function (c) { el.classList.add(c); });
        activateBars(el);
      });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        classes.forEach(function (c) { entry.target.classList.add(c); });
        activateBars(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -32px 0px' });

    els.forEach(function (el) { io.observe(el); });
  }

  /* Lazy CSS backgrounds (28 Oct 2026). A background-image is fetched as
     soon as its element is laid out, wherever it sits on the page, so two
     photos four screens down the homepage (the island chapter, 118 KB, and
     the harvest pour, 47 KB) downloaded alongside the hero and slowed it
     on phones. Elements marked [data-gi-lazy-bg] keep background-image:
     none (assets/gi-global.css) until they come within a screen and a half
     of the viewport; this adds "gi-bg-on" and the rule lets go. */
  function lazyBackgrounds() {
    var els = document.querySelectorAll('[data-gi-lazy-bg]');
    if (!els.length) return;
    if (noObserver) {
      els.forEach(function (el) { el.classList.add('gi-bg-on'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('gi-bg-on');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '150% 0px 150% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  function init() {
    lazyBackgrounds();
    reveal('.reveal', ['visible']);
    reveal('.gi-reveal', ['gi-on', 'gi-visible']);
    reveal('[data-reveal]', ['is-visible']);
    reveal('.gids-reveal', ['gids-in']);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
